/**
 * FunnelFields — shared controlled component for Funil section.
 *
 * Sources are simple jsonb (funnel_sources). Notes live in user_notes table
 * keyed by title — caller passes `notes` + `onNotesChange` and is responsible
 * for persisting the row (the existing wizard step debounces an upsert).
 */

import { useState, type KeyboardEvent } from "react";
import { X } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

export const PRESET_FUNNEL_SOURCES = [
  "Instagram",
  "TikTok",
  "YouTube",
  "Indicação",
  "Google Ads",
  "Facebook Ads",
  "Site próprio",
  "Parceiros",
];

export interface FunnelFieldsProps {
  sources: string[];
  onSourcesChange: (next: string[]) => void;
  notes: string;
  onNotesChange: (next: string) => void;
}

export function FunnelFields({
  sources,
  onSourcesChange,
  notes,
  onNotesChange,
}: FunnelFieldsProps) {
  const [chipInput, setChipInput] = useState("");

  const addSource = (s: string) => {
    const trimmed = s.trim();
    if (!trimmed || sources.includes(trimmed)) return;
    onSourcesChange([...sources, trimmed]);
  };

  const removeSource = (s: string) => {
    onSourcesChange(sources.filter((x) => x !== s));
  };

  const handleChipKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === ",") && chipInput.trim()) {
      e.preventDefault();
      addSource(chipInput);
      setChipInput("");
    } else if (e.key === "Backspace" && !chipInput && sources.length > 0) {
      removeSource(sources[sources.length - 1]);
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label>De onde vêm seus clientes</Label>

        <div className="flex flex-wrap gap-2">
          {PRESET_FUNNEL_SOURCES.map((s) => {
            const active = sources.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => (active ? removeSource(s) : addSource(s))}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-muted-foreground hover:border-primary hover:text-foreground"
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>

        <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 focus-within:ring-1 focus-within:ring-ring">
          {sources
            .filter((s) => !PRESET_FUNNEL_SOURCES.includes(s))
            .map((s) => (
              <Badge key={s} variant="secondary" className="gap-1 pr-1">
                {s}
                <button
                  type="button"
                  onClick={() => removeSource(s)}
                  className="rounded-full hover:bg-muted"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          <input
            value={chipInput}
            onChange={(e) => setChipInput(e.target.value)}
            onKeyDown={handleChipKeyDown}
            placeholder={sources.length === 0 ? "Outro canal… (Enter para adicionar)" : ""}
            className="min-w-24 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Selecione os canais acima ou digite um personalizado e pressione Enter.
        </p>
      </div>

      <div className="space-y-1.5 border-t border-border pt-4">
        <Label htmlFor="onboarding-notes">Anotações livres (opcional)</Label>
        <Textarea
          id="onboarding-notes"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Qualquer contexto extra que você queira que a IA leve em conta — observações sobre seu nicho, sazonalidade, restrições, oportunidades."
          rows={4}
          className="resize-none"
          maxLength={4000}
        />
        <p className="text-xs text-muted-foreground">
          Tudo aqui entra no contexto da IA junto com os outros campos.
        </p>
      </div>
    </div>
  );
}
